import React, { createContext, useContext, useEffect, useState } from 'react';
import { Batch } from '../../types/Voucher';
import { getBatches } from '../../apis';

const PAGE_LIMIT = 10;

type VoucherBatchesListContextType = {
  batches: Batch[];
  page: number;
  totalPages: number;
  totalCount: number;
  loading: boolean;
  error: Error | null;
  // eslint-disable-next-line no-unused-vars
  setPage: (page: number) => void;
  refetch: () => void;
};

const VoucherBatchesListContext = createContext<VoucherBatchesListContextType | undefined>(
  undefined,
);

export const useVoucherBatchesList = () => {
  const context = useContext(VoucherBatchesListContext);
  if (!context) {
    throw new Error('useVoucherBatchesList must be used within VoucherBatchesListProvider');
  }
  return context;
};

export const VoucherBatchesListProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<Error | null>(null);

  // The endpoint paginates server-side (page/limit) and scopes to the current
  // user; it takes no prefix/status filter params.
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBatches(page, PAGE_LIMIT);
      setBatches(Array.isArray(data.results) ? data.results : []);
      setTotalCount(data.count);
      setTotalPages(Math.max(1, Math.ceil(data.count / PAGE_LIMIT)));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError(err as Error);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <VoucherBatchesListContext.Provider
      value={{
        batches,
        page,
        totalPages,
        totalCount,
        loading,
        error,
        setPage,
        refetch: fetchData,
      }}
    >
      {children}
    </VoucherBatchesListContext.Provider>
  );
};
